import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import PauseIcon from "@mui/icons-material/Pause";
import { Box, type SxProps, Typography } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import type { GridRenderCellParams, GridValidRowModel } from "@mui/x-data-grid";
import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { memo, type ReactNode } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL, STAFF_PICKER, STATUS, STATUS_BADGE, SURFACE } from "@/theme/tokens";
import type { SystemGridColDef } from "@/ui/Tables/SystemDataGrid";
import type { PanelShiftData } from "@/utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";

/**
 * Class applied (via `cellClassName`/`headerClassName`) to day columns whose index is in
 * `highlightDateIndexes`. Grids paint the left/right rails + header top border for it via
 * `highlightedColumnSx` (spread into the grid `sx`).
 */
export const HIGHLIGHTED_COLUMN_CLASS = "nurses-matrix-highlighted-column";

/** Field id of the day column at `dayIndex`. Shared with the "Days" column-picker toggle so the
 * synthetic picker row and the actual columns it hides always reference the same fields. */
export const getDayColumnField = (dayIndex: number): string => `day_${dayIndex}`;

/**
 * Brand rails on the highlighted day column (the requested shift's day). Spread into a grid's `sx` so
 * the `HIGHLIGHTED_COLUMN_CLASS` set on that column's cells/header renders the left/right rails + a
 * header top border. The last-row bottom cap is intentionally omitted — best-effort under virtualization.
 */
export const highlightedColumnSx = {
	[`& .MuiDataGrid-cell.${HIGHLIGHTED_COLUMN_CLASS}`]: {
		borderLeft: `2px solid ${STATUS_BADGE.BRAND_FALLBACK}`,
		borderRight: `2px solid ${STATUS_BADGE.BRAND_FALLBACK}`,
		backgroundColor: NEUTRAL[50],
	},
	[`& .MuiDataGrid-columnHeader.${HIGHLIGHTED_COLUMN_CLASS}`]: {
		borderTop: `2px solid ${STATUS_BADGE.BRAND_FALLBACK}`,
		borderLeft: `2px solid ${STATUS_BADGE.BRAND_FALLBACK}`,
		borderRight: `2px solid ${STATUS_BADGE.BRAND_FALLBACK}`,
	},
} as const satisfies SxProps;

/** Minimal row shape the day cells read. Both `NurseData` and the minimized picker row satisfy it. */
export interface DayShiftRow {
	id: number;
	/** Grid row key when a nurse is listed more than once (see `NurseData.rowKey`). */
	rowKey?: string;
	/** In the current pick — shows the pending "+" on the target day (see `NurseData.picked`). */
	picked?: boolean;
	shifts: PanelShiftData[];
	selectable?: boolean;
	/** Set on a row that can't take the shift — its week strip is muted (see `NurseData.unavailableReason`). */
	unavailableReason?: string;
	accepted_start_time?: DateTime;
	accepted_end_time?: DateTime;
}

export interface BuildDayColumnsConfig {
	dateRange: DateTime[];
	highlightDateIndexes: number[];
	hightlightOriginalShift: boolean;
	/** True when the given nurse is a partial-shift acceptor for the active request. */
	getIsPartial: (nurseId: number) => boolean;
	/** Real hospital demo (inhouse2 + Hospital) only — renders the day/night icons in dark ink. */
	darkShiftIcons: boolean;
	/**
	 * Day index that previews the shift being added: on every PICKED row (selection modes only) that day
	 * shows a dashed "+" ring. Omit it to show no preview.
	 */
	pendingDayIndex?: number;
	t: TFunction;
}

/** Shift types that draw an icon in a day cell — a pending "+" has to make room for these. */
const ICON_SHIFT_TYPES = new Set(["day", "morning", "night", "off"]);

const hasShiftGlyph = (shift: PanelShiftData | undefined): boolean => !!shift && (!!shift.unavailableStatus || ICON_SHIFT_TYPES.has(shift.shiftType ?? ""));

const pendingEnter = keyframes`
	from { opacity: 0; transform: scale(${STAFF_PICKER.PENDING_ENTER_SCALE}); }
	to { opacity: 1; transform: scale(1); }
`;

/**
 * The dashed "+" previewing the shift a picked row is about to get. Alone on an empty day it is the full
 * ring; on a day that already shows a shift icon it shrinks to a superscript badge so it never covers the
 * shift the nurse already has. Lands with a short scale/opacity settle (reduced-motion: none).
 */
const PendingDayMarker = memo(function PendingDayMarker({ compact, t }: { compact: boolean; t: TFunction }) {
	const size = compact ? STAFF_PICKER.PENDING_MARKER_BADGE_SIZE : STAFF_PICKER.PENDING_MARKER_SIZE;
	const glyphSize = compact ? STAFF_PICKER.PENDING_GLYPH_SIZE_COMPACT : STAFF_PICKER.PENDING_GLYPH_SIZE;
	return (
		<Box
			role="img"
			aria-label={t("nurses.matrix.pending.shift")}
			sx={{
				position: "absolute",
				...(compact ? { top: STAFF_PICKER.PENDING_BADGE_OFFSET, left: `calc(50% + ${STAFF_PICKER.PENDING_BADGE_SHIFT_X}px)` } : { inset: 0, m: "auto" }),
				width: size,
				height: size,
				boxSizing: "border-box",
				borderRadius: "50%",
				border: `${STAFF_PICKER.PENDING_MARKER_BORDER}px dashed ${STATUS_BADGE.BRAND_FALLBACK}`,
				backgroundColor: SURFACE,
				color: STATUS_BADGE.BRAND_FALLBACK,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				"& > svg": { fontSize: glyphSize },
				animation: `${pendingEnter} ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
				"@media (prefers-reduced-motion: reduce)": { animation: "none" },
			}}
		>
			<AddRoundedIcon />
		</Box>
	);
});

/** Hosts a day cell's own content plus the (absolutely-positioned) pending marker over it. */
function PendingDaySlot({ picked, compact, t, children }: { picked: boolean; compact: boolean; t: TFunction; children: ReactNode }) {
	return (
		<Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
			{children}
			{picked && <PendingDayMarker compact={compact} t={t} />}
		</Box>
	);
}

const DayShiftCell = memo(function DayShiftCell({
	shift,
	dayIndex,
	selectable,
	isHighlighted,
	isPartial,
	hightlightOriginalShift,
	acceptedStartTime,
	acceptedEndTime,
	darkShiftIcons,
	muted,
	t,
}: {
	shift: PanelShiftData | undefined;
	dayIndex: number;
	selectable: boolean;
	isHighlighted: boolean;
	isPartial: boolean;
	hightlightOriginalShift: boolean;
	acceptedStartTime: DateTime | undefined;
	acceptedEndTime: DateTime | undefined;
	darkShiftIcons: boolean;
	/** A blocked row: its shifts stay legible as context for the reason, but recede. */
	muted: boolean;
	t: TFunction;
}) {
	if (!shift) return null;
	const isOriginalShift = hightlightOriginalShift && shift.originalDayIndex === dayIndex;
	return (
		<InHouseTooltip title={shift.tooltip || ""} arrow disableHoverListener={!shift.tooltip || !selectable}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					position: "relative",
					width: "100%",
					height: "100%",
					cursor: shift.shiftType === null ? "pointer" : "default",
					...(muted && { opacity: STAFF_PICKER.BLOCKED_STRIP_OPACITY }),
					...(isOriginalShift && { border: `2px solid ${STATUS_BADGE.BRAND_FALLBACK}`, backgroundColor: NEUTRAL[50] }),
				}}
			>
				{shift.unavailableStatus ? (
					<PauseIcon
						aria-label={shift.unavailableStatus === "Suspended" ? t("employee.status.chip.suspended") : t("employee.status.chip.on_leave")}
						sx={{ fontSize: 18, color: shift.unavailableStatus === "Suspended" ? STATUS.ERROR.main : STATUS.WARNING.main }}
					/>
				) : (
					<>
						{/* Demo (inhouse2 hospital) renders the day/night icons in dark ink via a brightness filter. */}
						{(shift.shiftType === "day" || shift.shiftType === "morning") && (
							<Box component="img" src="/sun.svg" alt={t("day.shift")} sx={{ width: 16, height: 16, ...(darkShiftIcons && { filter: "brightness(0)" }) }} />
						)}
						{shift.shiftType === "night" && (
							<Box component="img" src="/moon.svg" alt={t("night.shift")} sx={{ width: 16, height: 16, ...(darkShiftIcons && { filter: "brightness(0)" }) }} />
						)}
						{shift.shiftType === "off" && <Box component="img" src="/off.svg" alt={t("off.shift")} sx={{ width: 16, height: 16 }} />}
					</>
				)}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					{shift.hasWarning && (
						<Box component="img" src="/thumbsdown.svg" alt={t("nurses.matrix.dislike")} style={{ zIndex: 2 }} sx={{ width: 18, height: 18 }} />
					)}
					{isHighlighted && isPartial && acceptedStartTime && acceptedEndTime && (
						<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: STATUS_BADGE.BRAND_FALLBACK }}>
							{(() => {
								const hours = acceptedEndTime.diff(acceptedStartTime, "hour").as("hours");
								return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
							})()}
						</Typography>
					)}
				</Box>
				{isHighlighted && (shift.isRecurringShift || shift.isFromMasterSchedule) && (
					<AutorenewOutlinedIcon fontSize="small" style={{ color: alpha(INK.SECONDARY, 0.5) }} />
				)}
			</Box>
		</InHouseTooltip>
	);
});

// Shared default spread into every column. `display: "flex"` vertically centers cell content (MUI X
// top-aligns custom `renderCell` output by default) while still honoring each column's `align`.
const columnBase = { display: "flex" } as const;

/**
 * Appends the N per-day shift columns (one per `dateRange` entry) to `columns`. Generic over any row that
 * satisfies `DayShiftRow`, so both the full `NursesMatrixGrid` and the minimized request-approval picker
 * share one builder. Day columns are structural — excluded from the per-column picker list and toggled as a
 * block via the synthetic "Days" row (see `getDayColumnField`).
 */
export function buildDayColumns<T extends DayShiftRow & GridValidRowModel>(config: BuildDayColumnsConfig, columns: SystemGridColDef<T>[]): void {
	const { dateRange, highlightDateIndexes, hightlightOriginalShift, getIsPartial, darkShiftIcons, pendingDayIndex, t } = config;
	dateRange.forEach((date, dayIndex) => {
		const isHighlighted = highlightDateIndexes.includes(dayIndex);
		columns.push({
			...columnBase,
			field: getDayColumnField(dayIndex),
			// Excluded from the picker's per-column list: the day columns are toggled as one block via the
			// synthetic "Days" row, never individually (single-letter headers are ambiguous).
			excludeFromColumnPicker: true,
			headerName: date.toFormat("ccc").charAt(0),
			width: 46,
			sortable: false,
			disableColumnMenu: true,
			disableExport: true,
			align: "center",
			headerAlign: "center",
			cellClassName: isHighlighted ? HIGHLIGHTED_COLUMN_CLASS : undefined,
			headerClassName: isHighlighted ? HIGHLIGHTED_COLUMN_CLASS : undefined,
			renderCell: (params: GridRenderCellParams<T>) => {
				const shift = params.row.shifts[dayIndex];
				const cell = (
					<DayShiftCell
						shift={shift}
						dayIndex={dayIndex}
						selectable={params.row.selectable !== false}
						isHighlighted={isHighlighted}
						isPartial={getIsPartial(params.row.id)}
						hightlightOriginalShift={hightlightOriginalShift}
						acceptedStartTime={params.row.accepted_start_time}
						acceptedEndTime={params.row.accepted_end_time}
						darkShiftIcons={darkShiftIcons}
						muted={params.row.unavailableReason != null}
						t={t}
					/>
				);
				// A blocked row can never be picked, so it never previews a pending shift.
				if (dayIndex !== pendingDayIndex || params.row.unavailableReason != null) return cell;
				return (
					<PendingDaySlot picked={params.row.picked === true} compact={hasShiftGlyph(shift)} t={t}>
						{cell}
					</PendingDaySlot>
				);
			},
		});
	});
}
